/**
 * useLastActive Hook (DD-160)
 *
 * Updates `profiles.last_seen_at` every time the app comes to the
 * foreground (AppState 'active' transition).
 *
 * Behaviour:
 * - Listens to React Native AppState changes
 * - On foreground: POSTs to `update-last-active` for the authenticated user
 * - Debounces rapid transitions (e.g., permission dialogs) with 2s guard
 * - No-ops for anonymous users (no session)
 * - Errors are logged but never block the user
 *
 * Must be called inside AuthProvider and React Query provider.
 *
 * DTO migration (Wave 4A): the response (unused by the caller today) is
 * validated through the shared `UpdateLastActiveResultDto` via
 * `fetchJsonValidated`, so the endpoint is still exercised by the full
 * wire-contract invariant even though it's fire-and-forget telemetry.
 */

import { useEffect, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import { UpdateLastActiveResultDto } from '@dramadetector/shared';
import { getEdgeFunctionUrl } from '../lib/api-url';

/** Minimum interval between last_seen_at updates (ms) */
const DEBOUNCE_MS = 2_000;

export function useLastActive(): void {
  const { accessToken, user } = useAuth();
  const lastUpdateRef = useRef<number>(0);

  useEffect(() => {
    // Phase 2 (NocoDB #3844): skip for anon BA users. The endpoint uses strict
    // `auth` middleware (allowAnonymous: false) which correctly returns 401 for
    // anon callers — firing the request is just wasted traffic.
    if (!accessToken || !user?.id || user.isAnonymous) return;

    const updateLastSeen = async () => {
      const now = Date.now();
      if (now - lastUpdateRef.current < DEBOUNCE_MS) return;
      lastUpdateRef.current = now;

      try {
        await fetchJsonValidated(
          UpdateLastActiveResultDto,
          getEdgeFunctionUrl('update-last-active'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          },
        );
      } catch (err) {
        console.warn('[useLastActive] Failed to update last_seen_at:', err);
      }
    };

    // Update immediately on mount (app just opened or user just logged in)
    updateLastSeen();

    // Listen for foreground transitions
    const subscription = AppState.addEventListener(
      'change',
      (nextState: AppStateStatus) => {
        if (nextState === 'active') {
          updateLastSeen();
        }
      },
    );

    return () => {
      subscription.remove();
    };
  }, [accessToken, user?.id]);
}
